import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useListenKey } from '@/lib/api/listen-key';
import { logConsole } from '@/lib/utils';
import { useAccountStore } from '@/store/use-account-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { ErrorCode, WsErrorCode } from '@/types';

import { Button } from '../ui';

export default function RealtimeConnectionLostBanner() {
  const { t } = useTranslation();

  const { mutateAsync: getListenKey } = useListenKey();
  const [isRealTimeConnectionLost, setRealTimeConnectionLost] = useState({
    private: false,
    public: false,
  });

  const hasPublicWs = useWebSocketStore((state) => !!state.publicWs);

  useEffect(() => {
    if (!hasPublicWs) return;
    const { publicWs } = useWebSocketStore.getState();
    if (!publicWs) return;
    publicWs.attachOnConnect(() =>
      setRealTimeConnectionLost((prev) => ({ ...prev, public: false })),
    );
    const onDisconnect = (error?: string | ErrorCode) => {
      setRealTimeConnectionLost((prev) => ({ ...prev, public: true }));
      if (error) {
        logConsole(true, 'PublicWS Error', error);
      }
    };
    publicWs.attachOnDisconnect(onDisconnect, 1);
  }, [hasPublicWs]);

  const accountAddress = useAccountStore((state) => state.address);
  const privateWs = useWebSocketStore((state) => {
    if (!accountAddress) return null;
    return state.privateWsByAddress[accountAddress.toLowerCase()] ?? null;
  });

  useEffect(() => {
    if (!accountAddress || !privateWs) return;
    privateWs.attachOnConnect(() => {
      useOrdersStore.setState({ refetchInterval: 15_000 });
      setRealTimeConnectionLost((prev) => ({ ...prev, private: false }));
    });
    privateWs.attachOnDisconnect(() => {
      const { hasAuth } = useAccountStore.getState();
      if (!hasAuth) return;
      useOrdersStore.setState({ refetchInterval: 1_500 });
      setRealTimeConnectionLost((prev) => ({ ...prev, private: true }));
    }, 1);
    privateWs.attachOnError((error) => {
      logConsole(true, 'PrivateWS Error', error);
      const accGroup = useAccountStore
        .getState()
        .getAuthParams(accountAddress, { skipFallback: true });
      if (accGroup === undefined || accGroup.accountGroup === 2) return;
      switch (error) {
        case WsErrorCode.LISTEN_KEY_NOT_FOUND:
        case WsErrorCode.LISTEN_KEY_EXPIRED:
          getListenKey(accountAddress);
          break;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountAddress, privateWs]);

  if (Object.values(isRealTimeConnectionLost).every((v) => !v)) return;

  return (
    <div className="relative z-50 flex items-center gap-4 bg-vestgrey-100 px-6 py-4 text-vestgrey-900 dark:bg-vestgrey-850 dark:text-vestgrey-50 sm:px-8">
      <p className="text-sm tracking-wider sm:text-base lg:text-lg">
        <strong>{t('realtime.connectionLostTitle')}</strong>
        &nbsp;{t('realtime.connectionLostDescription')}
      </p>
      <Button
        onClick={() => window.location.reload()}
        className="rounded-none font-mono font-normal tracking-wider"
      >
        {t('realtime.refreshPage')}
      </Button>
    </div>
  );
}
